<template>
  <b-modal v-model="open" title="Fotos do caixa" size="xl" hide-footer>
    <b-alert v-if="photos.error" show variant="danger">
      <span class="fas fa-exclamation-triangle mr-1" />
      {{ photos.error }}
    </b-alert>
    <b-alert v-if="depositReceipts.error" show variant="danger">
      <span class="fas fa-exclamation-triangle mr-1" />
      {{ depositReceipts.error }}
    </b-alert>
    <b-alert v-if="expenseReceipts.error" show variant="danger">
      <span class="fas fa-exclamation-triangle mr-1" />
      {{ expenseReceipts.error }}
    </b-alert>

    <b-row v-if="canManage" class="mb-3">
      <b-col sm="12" md="3">
        <b-form-group label="Tipo" label-for="checkout-photo-type">
          <b-form-select
            id="checkout-photo-type"
            v-model="form.type"
            :options="typeOptions"
          />
        </b-form-group>
      </b-col>
      <b-col sm="12" md="3">
        <b-form-group label="Foto" label-for="checkout-photo-file">
          <b-form-file
            id="checkout-photo-file"
            accept="image/*"
            browse-text="Escolher"
            placeholder="Selecione a imagem"
            @input="onPhotoInput"
          />
        </b-form-group>
      </b-col>
      <b-col sm="12" md="4">
        <b-form-group label="Descricao" label-for="checkout-photo-description">
          <b-form-input
            id="checkout-photo-description"
            v-model="form.description"
            placeholder="Descricao opcional"
          />
        </b-form-group>
      </b-col>
      <b-col sm="12" md="2" class="photo-form-actions">
        <b-button variant="primary" :disabled="!form.photo" @click="addPhoto">
          <span class="fas fa-paperclip mr-1" />
          Anexar
        </b-button>
      </b-col>
    </b-row>

    <b-table
      responsive
      :fields="fields"
      :items="allPhotos"
      :busy="
        photos.loading || depositReceipts.loading || expenseReceipts.loading
      "
      striped
      hover
      show-empty
    >
      <div slot="empty" class="text-center">Nenhuma foto anexada</div>

      <template #cell(type)="{ value: type }">
        {{ CHECKOUT_PHOTO_TYPE_LABELS[type] || type }}
      </template>

      <template #cell(description)="{ value: description }">
        {{ description || '-' }}
      </template>

      <template #cell(createdBy)="{ item }">
        {{ item.createdByUser ? item.createdByUser.name : '-' }}
      </template>

      <template #cell(createdAt)="{ value: createdAt }">
        {{ createdAt | formatDateTime }}
      </template>

      <template #cell(actions)="{ item }">
        <div class="photo-actions">
          <b-button
            v-b-tooltip.hover
            size="sm"
            variant="outline-primary"
            title="Ver foto"
            @click="openPhoto(item)"
          >
            <span class="fas fa-image" />
          </b-button>
          <b-button
            v-if="canManage && item.source === 'checkoutPhoto'"
            v-b-tooltip.hover
            size="sm"
            variant="outline-danger"
            title="Remover foto"
            @click="deletePhoto(item)"
          >
            <span class="fas fa-trash" />
          </b-button>
        </div>
      </template>
    </b-table>

    <b-modal
      v-model="photoPreview.visible"
      title="Foto do caixa"
      size="lg"
      hide-footer
    >
      <img
        v-if="photoPreview.url"
        :src="photoPreview.url"
        alt="Foto do caixa"
        class="checkout-photo-preview"
      />
    </b-modal>
  </b-modal>
</template>

<script>
import withAsyncAction from '@/mixins/withAsyncAction'
import services from '@/services'
import { CHECKOUT_PHOTO_TYPES, CHECKOUT_PHOTO_TYPE_LABELS } from '@/constants'
import { formatDateTime } from '@/helpers/date'

const DEPOSIT_RECEIPT_TYPE = 'depositReceipt'
const EXPENSE_RECEIPT_TYPE = 'expenseReceipt'
const PHOTO_TYPE_LABELS = {
  ...CHECKOUT_PHOTO_TYPE_LABELS,
  [DEPOSIT_RECEIPT_TYPE]: 'Comprovante de deposito',
  [EXPENSE_RECEIPT_TYPE]: 'Nota/comprovante de saida'
}

export default {
  name: 'CheckoutPhotosModal',
  filters: {
    formatDateTime
  },
  mixins: [
    withAsyncAction({
      key: 'photos',
      initialState: [],
      fetcher: {
        methodName: 'fetchCheckoutPhotos',
        handler: services.checkouts.fetchCheckoutPhotos
      }
    }),
    withAsyncAction({
      key: 'depositReceipts',
      initialState: [],
      fetcher: {
        methodName: 'fetchDepositReceipts',
        handler: services.operations.fetchBankDeposits
      }
    }),
    withAsyncAction({
      key: 'expenseReceipts',
      initialState: [],
      fetcher: {
        methodName: 'fetchExpenseReceipts',
        handler: services.operations.fetchCashExpenses
      }
    })
  ],
  props: {
    value: {
      type: Boolean
    },
    checkout: {
      type: Object,
      required: true
    },
    canManage: {
      type: Boolean
    }
  },
  data() {
    return {
      form: {
        type: CHECKOUT_PHOTO_TYPES.CLOSING_NOTEBOOK,
        photo: null,
        description: ''
      },
      photoPreview: {
        visible: false,
        url: null
      }
    }
  },
  computed: {
    open: {
      set(value) {
        this.$emit('input', value)
      },
      get() {
        return this.value
      }
    },
    fields() {
      return [
        {
          key: 'type',
          label: 'Tipo'
        },
        {
          key: 'description',
          label: 'Descricao'
        },
        {
          key: 'createdBy',
          label: 'Anexado por'
        },
        {
          key: 'createdAt',
          label: 'Anexado em'
        },
        {
          key: 'actions',
          label: 'Acoes'
        }
      ]
    },
    allPhotos() {
      const checkoutPhotos = this.photos.data.map((photo) => ({
        ...photo,
        source: 'checkoutPhoto'
      }))
      const depositReceipts = this.depositReceipts.data
        .filter((operation) => operation.receiptPhotoUrl)
        .map((operation) => ({
          id: `deposit-${operation.id}`,
          source: 'depositReceipt',
          type: DEPOSIT_RECEIPT_TYPE,
          photoUrl: operation.receiptPhotoUrl,
          description: operation.name,
          createdByUser:
            operation.receiptPhotoUploadedByUser || operation.createdByUser,
          createdAt:
            operation.receiptPhotoUploadedAt ||
            operation.updatedAt ||
            operation.createdAt
        }))

      const expenseReceipts = this.expenseReceipts.data
        .filter((operation) => operation.receiptPhotoUrl)
        .map((operation) => ({
          id: `expense-${operation.id}`,
          source: 'expenseReceipt',
          type: EXPENSE_RECEIPT_TYPE,
          photoUrl: operation.receiptPhotoUrl,
          description: operation.name,
          createdByUser:
            operation.receiptPhotoUploadedByUser || operation.createdByUser,
          createdAt:
            operation.receiptPhotoUploadedAt ||
            operation.updatedAt ||
            operation.createdAt
        }))

      return [...checkoutPhotos, ...depositReceipts, ...expenseReceipts]
    },
    typeOptions() {
      return Object.values(CHECKOUT_PHOTO_TYPES).map((value) => ({
        value,
        text: CHECKOUT_PHOTO_TYPE_LABELS[value]
      }))
    }
  },
  watch: {
    open(value) {
      if (value) {
        this.loadPhotos()
      }
    }
  },
  created() {
    this.CHECKOUT_PHOTO_TYPE_LABELS = PHOTO_TYPE_LABELS
  },
  methods: {
    async loadPhotos() {
      await Promise.all([
        this.fetchCheckoutPhotos({
          checkoutId: this.checkout.id
        }),
        this.fetchDepositReceipts({
          checkoutId: this.checkout.id
        }),
        this.fetchExpenseReceipts({
          checkoutId: this.checkout.id
        })
      ])
    },
    onPhotoInput(file) {
      this.form.photo = file
    },
    async addPhoto() {
      await services.checkouts.addCheckoutPhoto({
        checkoutId: this.checkout.id,
        ...this.form
      })
      this.$root.$toast.success('Foto anexada com sucesso')
      this.form.photo = null
      this.form.description = ''
      await this.loadPhotos()
      this.$emit('refresh')
    },
    openPhoto(photo) {
      this.photoPreview = {
        visible: true,
        url: photo.photoUrl
      }
    },
    async deletePhoto(photo) {
      const result = await this.$bvModal.msgBoxConfirm(
        'Deseja remover esta foto?',
        {
          title: 'Remover foto',
          okTitle: 'Remover',
          okVariant: 'danger',
          cancelTitle: 'Cancelar',
          centered: true
        }
      )

      if (!result) return

      await services.checkouts.deleteCheckoutPhoto(photo.id)
      this.$root.$toast.success('Foto removida com sucesso')
      await this.loadPhotos()
      this.$emit('refresh')
    }
  }
}
</script>

<style scoped>
.photo-form-actions {
  align-items: flex-end;
  display: flex;
  padding-bottom: 1rem;
}

.photo-actions {
  display: flex;
  gap: 0.25rem;
}

.checkout-photo-preview {
  display: block;
  max-height: 70vh;
  object-fit: contain;
  width: 100%;
}
</style>
